import { Trans, useLingui } from "@lingui/react/macro";
import { Heading, Link, Text } from "@offstreetparking/design-system/typography";
import type { InvoiceRateSegmentItem } from "@offstreetparking/domain/entities/invoice";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePortalFlowHost } from "../../../../host";
import { PRIVACY_URL, TERMS_URL } from "../../../../lib/legal-urls";
import { ProrationInfoBanner } from "../../../dataDisplay/parkingDetails";
import { PaymentErrorAlert } from "../../../forms/Alert";
import { usePermitFormActions, usePermitFormData } from "../../PermitContextProvider";
import { usePermitWizardSettings } from "../../PermitContextProvider/PermitContextProvider";
import type { PermitFormData, PermitPostResultData } from "../../PermitContextProvider/types";
import { useSavedPermits } from "../../hooks/useSavedPermits";
import type { PermitTypeDto } from "../../types";
import { InvoiceDetails } from "./components/InvoiceDetails";
import type { PaymentError, PaymentErrorHandler } from "./types";
import { usePaymentSession } from "./usePaymentSession";
export type PaymentFormProps = {
	permitType: PermitTypeDto;
	locationLookupId?: string;
	tenantId?: number;
	time: {
		startDateTime: string;
		endDateTime?: string;
		timezone: string;
	};
	invoice?: {
		total: number;
		subtotal: number;
		taxAndFees: {
			description: string;
			longDescription?: string;
			percent?: number;
			fixedAmount?: number;
			total: number;
		}[];
		discountItems?: {
			description: string;
			total: number;
			type?: "Proration";
		}[];
		fixedRateItems?: {
			style: string;
			amount: number;
			units: number;
			total: number;
		}[];
		rateSegmentItems?: InvoiceRateSegmentItem[];
	};
	email?: string;
	vehicles?: {
		plate: string;
		state: string;
	}[];
	currency: string;
	locale: string;
	formId: string;
	onSubmit: (data: PermitPostResultData) => void;
	setDuplicatedPermit: ({
		plate,
		permitIntervals,
		publicIdentifier,
	}: {
		plate: string;
		permitIntervals: PermitFormData["permitIntervals"];
		publicIdentifier: string;
	}) => void;
};
export const PaymentForm = ({
	permitType,
	locationLookupId,
	tenantId,
	invoice,
	time,
	email,
	vehicles,
	locale,
	formId,
	onSubmit,
	setDuplicatedPermit,
}: PaymentFormProps) => {
	const { addPermit } = useSavedPermits();
	const { t } = useLingui();
	const { setIsProcessingPermit } = usePermitFormActions();
	const wizardSettings = usePermitWizardSettings();
	const permitFormData = usePermitFormData();
	const selectedChoice =
		wizardSettings.choices.find((choice) => choice.permitTypeId === permitFormData.parkingChoiceId) ??
		wizardSettings.choices[0];
	const [paymentError, setPaymentError] = useState<PaymentError>();
	const [isGatewayProcessing, setIsGatewayProcessing] = useState(false);
	const { paymentSession, isCreating, requestNewSession } = usePaymentSession({
		tenantId,
		locale,
		setDuplicatedPermit: (permit) => {
			setPaymentError(undefined);
			setDuplicatedPermit(permit);
		},
		onError: setPaymentError,
	});
	const { PaymentGatewayPanel: GatewayPanel } = usePortalFlowHost();
	const handlePaymentSuccess = useCallback(
		(publicIdentifier: string) => {
			if (locationLookupId) {
				addPermit(locationLookupId, {
					publicIdentifier,
					publicAccessToken: paymentSession?.permit?.publicAccessToken ?? "",
				});
			}
			onSubmit({
				publicIdentifier,
				publicAccessToken: paymentSession?.permit?.publicAccessToken ?? undefined,
			});
			setIsGatewayProcessing(false);
		},
		[addPermit, locationLookupId, onSubmit, paymentSession],
	);

	useEffect(() => {
		setIsProcessingPermit(isGatewayProcessing || isCreating);
		return () => setIsProcessingPermit(false);
	}, [isGatewayProcessing, isCreating, setIsProcessingPermit]);

	const setIsProcessing = (isProcessing: boolean) => {
		if (isProcessing) {
			setPaymentError(undefined);
		}
		setIsGatewayProcessing(isProcessing);
	};

	const handleError: PaymentErrorHandler = ({ error, needsNewSession }) => {
		setPaymentError(error);
		setIsGatewayProcessing(false);
		if (needsNewSession) {
			requestNewSession({ afterPaymentError: true });
		}
	};

	const paymentErrorRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (paymentError) {
			paymentErrorRef.current?.focus();
		}
	}, [paymentError]);

	return (
		<div className="space-y-4">
			<Heading>{t`Enter Payment Information`}</Heading>
			{invoice?.discountItems?.some((item) => item.type === "Proration") && (
				<ProrationInfoBanner
					fullTermIntervals={selectedChoice?.context?.fixedIntervals}
					timezone={selectedChoice?.time.timezone}
				/>
			)}
			<InvoiceDetails
				permitType={permitType}
				invoice={invoice}
				vehicles={vehicles}
				email={email}
				time={time}
				paymentSchedule={selectedChoice?.paymentSchedule}
			/>

			{paymentError && (
				<div ref={paymentErrorRef} tabIndex={-1}>
					<PaymentErrorAlert error={paymentError} />
				</div>
			)}
			{GatewayPanel ? (
				!paymentSession && !isCreating ? (
					<form
						id={formId}
						onSubmit={(e) => {
							e.preventDefault();
							setPaymentError(undefined);
							requestNewSession();
						}}
					/>
				) : (
					<GatewayPanel
						paymentSession={paymentSession}
						formId={formId}
						locationLookupId={locationLookupId}
						onSuccess={handlePaymentSuccess}
						onError={handleError}
						setIsProcessing={setIsProcessing}
					/>
				)
			) : (
				<PaymentErrorAlert />
			)}
			<Text className="my-4">
				<Trans>
					By continuing, I accept Offstreet’s{" "}
					<Link href={TERMS_URL} isExternal>
						Terms of Service
					</Link>{" "}
					and{" "}
					<Link href={PRIVACY_URL} isExternal>
						Privacy Policy
					</Link>
					.
				</Trans>
			</Text>
		</div>
	);
};
